// Scrollable tabbed strip with full-height content panel
import * as React from "react";
import "./CPTabbedStripStyles.scss";

export interface CPTabbedStripTabProps {
  title: React.ReactNode;
  children?: React.ReactNode;
}

export const CPTabbedStripTab: React.FC<CPTabbedStripTabProps> = ({
  children,
}) => <>{children}</>;

export interface CPTabbedStripProps {
  selected: number;
  onSelect: (index: number) => void;
  scrollable?: boolean;
  className?: string;
  children:
    | React.ReactElement<CPTabbedStripTabProps>
    | React.ReactElement<CPTabbedStripTabProps>[];
}

const SCROLL_STEP = 140;

const CPTabbedStrip: React.FC<CPTabbedStripProps> = ({
  selected,
  onSelect,
  scrollable = false,
  className,
  children,
}) => {
  const tabs = React.Children.toArray(
    children,
  ) as React.ReactElement<CPTabbedStripTabProps>[];

  const tablistRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const checkScroll = React.useCallback(() => {
    const el = tablistRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    if (!scrollable) return;
    checkScroll();
    const el = tablistRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      ro.disconnect();
    };
  }, [scrollable, checkScroll]);

  // Scroll active tab into view when selection changes
  React.useEffect(() => {
    if (!scrollable || !tablistRef.current) return;
    const el = tablistRef.current;
    const tabEl = el.children[selected] as HTMLElement | undefined;
    tabEl?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [selected, scrollable]);

  const handleScrollLeft = () => {
    tablistRef.current?.scrollBy({ left: -SCROLL_STEP, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    tablistRef.current?.scrollBy({ left: SCROLL_STEP, behavior: "smooth" });
  };

  const hasOverflow = canScrollLeft || canScrollRight;

  return (
    <div className={`cp-tabstrip${className ? ` ${className}` : ""}`}>
      <div className="cp-tabstrip-tablist-wrapper">
        {scrollable && (
          <button
            className={`cp-tabstrip-scroll-btn${canScrollLeft ? "" : " disabled"}`}
            onClick={handleScrollLeft}
            disabled={!canScrollLeft}
            tabIndex={-1}
            aria-label="Scroll tabs left"
            type="button"
          >
            <i className="fa-solid fa-chevron-left" />
          </button>
        )}

        <div
          className={`cp-tabstrip-tablist${scrollable ? " scrollable" : ""}${hasOverflow ? " has-overflow" : ""}`}
          ref={tablistRef}
          role="tablist"
        >
          {tabs.map((tab, idx) => (
            <button
              key={idx}
              role="tab"
              aria-selected={selected === idx}
              className={`cp-tabstrip-tab${selected === idx ? " active" : ""}`}
              onClick={() => onSelect(idx)}
              type="button"
            >
              {tab.props.title}
            </button>
          ))}
        </div>

        {scrollable && (
          <button
            className={`cp-tabstrip-scroll-btn${canScrollRight ? "" : " disabled"}`}
            onClick={handleScrollRight}
            disabled={!canScrollRight}
            tabIndex={-1}
            aria-label="Scroll tabs right"
            type="button"
          >
            <i className="fa-solid fa-chevron-right" />
          </button>
        )}
      </div>

      <div className="cp-tabstrip-content" role="tabpanel">
        {tabs[selected]?.props.children ?? null}
      </div>
    </div>
  );
};

export default CPTabbedStrip;
